import CommonTable, { type TableColumn } from '@/components/common/CommonTable'
import MainHeading from '@/components/common/MainHeading'
import Layout from '@/components/layout/Layout'
import { RoutesLink } from '@/utility/RouteLinks'
import { Eye, Pencil, Plus, MoreHorizontal, Upload } from 'lucide-react'
import React, { useEffect } from 'react'
import { useNavigate } from 'react-router-dom'
import { Button } from "@/components/ui/button"
import {
    DropdownMenu,
    DropdownMenuContent,
    DropdownMenuItem,
    DropdownMenuTrigger,
} from "@/components/ui/dropdown-menu"
import { useState } from 'react'
import EmployeeOnBoardService from '@/services/EmployeeOnBoard/EmployeeOnBoardService'
import { ThrowError } from '@/api/callApi'
import { API_URL } from '@/api/constant'
import type { IOAuthClient, IUser } from '@/interface/employeeOnBoard'
import moment from 'moment'
import type { IPageInfo } from '@/interface/common.interface'
import TablePagination from '@/components/common/TablePagination'
import CommonFilter from '@/components/common/CommonFilter'
import { GetFilterUrl } from '@/utility/helper'
import type { IFilter } from '@/interface/common.interface'

import { Tabs, TabsList, TabsTrigger, TabsContent } from "@/components/ui/tabs";
import { useAppFilters } from '@/hooks/useAppFilters'

const STATUS_OPTIONS = [
    // { label: "All", value: "" },
    { label: "Current", value: "current" },
    { label: "Ex-Employee", value: "exEmployee" },
];

const Employees: React.FC = () => {
    const navigate = useNavigate()
    const [isLoading, setIsLoading] = useState(false)
    const [employees, setEmployees] = useState<IUser[]>([]);
    const [activeTab, setActiveTab] = useState<"all" | "complete" | "in_process">("all");

    const {
        filterValues,
        queryString: filterQueryString,
        filterSelectorsNode,
    } = useAppFilters({
        enabled: ['branch', 'site', 'department', 'designation'],
        onChange: () => {
            setPaginationData((prev) => ({ ...prev, currentPage: 1 }));
        },
    });

    const [filter, setFilter] = useState<IFilter & { isVerified?: number }>({
        search: "",
        status: "current",
        isVerified: undefined,
    });

    const [paginationData, setPaginationData] = useState<IPageInfo>({
        currentPage: 1,
        pageSize: 10,
        total: 0,
        totalPages: 0,
    });

    const handleChangePageSize = (value: number) => {
        setPaginationData((prev) => ({ ...prev, pageSize: value, currentPage: 1 }));
    };

    const handleSearchChange = (value: string) => {
        setFilter((prev) => {
            if (prev.search === value) return prev;
            return { ...prev, search: value };
        });
        setPaginationData((prev) => ({ ...prev, currentPage: 1 }));
    };

    const handleFilterChange = (value: string, name: string) => {
        setFilter((prev) => ({ ...prev, [name]: value }));
        setPaginationData((prev) => ({ ...prev, currentPage: 1 }));
    };


    const handleGetEmployees = async (currentPage = paginationData.currentPage, currentLimit = paginationData.pageSize) => {
        setIsLoading(true)
        try {
            const params = GetFilterUrl({ ...filter });
            const PATH = `pageNumber=${currentPage}&pageSize=${currentLimit}${params ? `&${params}` : ""}${filterQueryString}`;
            const response = await EmployeeOnBoardService.getAllEmployees(PATH)
            if (response?.status) {
                // Ensure users array falls back correctly
                setEmployees(response?.data?.user || [])
                setPaginationData((prev) => ({
                    ...prev,
                    total: response?.data?.pageInfo?.total || 0,
                    totalPages: response?.data?.pageInfo?.totalPages || 0,
                }));
            }
        } catch (error) {
            ThrowError(error)
        } finally {
            setIsLoading(false)
        }
    }

    useEffect(() => {
        handleGetEmployees(paginationData.currentPage, paginationData.pageSize)
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, [paginationData.currentPage, paginationData.pageSize, filter, filterValues.branchId, filterValues.siteId, filterValues.departmentId, filterValues.designationId])

    const columns: TableColumn<IUser>[] = [
        {
            key: 'sNo',
            header: 'S.No',
            render: (_item: IUser, index: number) => (
                <span className="text-muted-foreground font-medium text-sm">{String(
                    (paginationData.currentPage - 1) * paginationData.pageSize + index + 1
                ).padStart(2, "0")}</span>
            )
        },
        {
            key: 'name',
            header: 'Name',
            render: (item: IUser) => (
                <div className="flex items-center gap-3">
                    <span className='flex items-center gap-2 '>{item?.profilePhoto ? <img src={API_URL + "/" + item?.profilePhoto} alt='' className='h-10 w-10 min-w-10 rounded-full inline-block' /> : <div className="h-9 w-9 rounded-full bg-primary/10 flex items-center justify-center text-primary font-bold shadow-sm mr-2 cursor-pointer">{item?.name?.charAt(0)?.toUpperCase()}</div>}  <span className='cursor-pointer hover:text-primary underline' onClick={() => navigate(`${RoutesLink.editEmployee}/${item.id}`)}>{item?.name}</span></span>
                </div>
            )
        },
        {
            key: 'email',
            header: 'Email',
            render: (item: IUser) => (
                <span className="font-medium text-gray-700 ">{item?.email || '--'}</span>
            )
        },
        {
            key: 'phone',
            header: 'Phone',
            render: (item: IUser) => (
                <span className="font-medium text-gray-700">{item?.phone || '--'}</span>
            )
        },
        {
            key: 'branch',
            header: 'Branch',
            render: (item: IUser) => (
                <span className="font-medium text-gray-700">{item?.branch?.name || '--'}</span>
            )
        },

        {
            key: 'site',
            header: 'Site',
            render: (item: IUser) => (
                <span className="font-medium text-gray-700">{item?.site?.name || '--'}</span>
            )
        },

        {
            key: 'campus',
            header: 'Campus',
            render: (item: IUser) => (
                <span className="font-medium text-gray-700">{item?.site?.campuses[0]?.name || '--'}</span>
            )
        },
        {
            key: 'department',
            header: 'Department',
            render: (item: IUser) => (
                <span className="font-medium text-gray-700">{item?.department?.name || '--'}</span>
            )
        },
        {
            key: 'designation',
            header: 'Designation',
            render: (item: IUser) => (
                <span className="text-gray-600">{item?.designation?.name || '--'}</span>
            )
        },
        {
            key: 'shift',
            header: 'Shift',
            render: (item: IUser) => (
                <span className="px-2.5 py-1 bg-gray-100 text-gray-700 rounded-md text-xs font-medium border border-gray-200">
                    {item?.shift?.shiftName || '--'}
                </span>
            )
        },
        {
            key: 'Application',
            header: 'Applications',
            render: (item: IUser) => (
                <span className="text-gray-600 truncate max-w-[150px] inline-block">{item?.oauthClients?.map((client: IOAuthClient) => client?.name).join(', ') || 'N/A'}</span>
            )
        },
        {
            key: 'application.employeeType',
            header: ' App Emp Type',
            render: (item: IUser) => (
                <span className="font-medium text-gray-700">{item?.type === 0 ? 'Staff' : 'Admin'}</span>
            )
        },
        {
            key: 'joiningDate',
            header: 'Joining Date',
            render: (item: IUser) => (
                <span className="font-medium text-gray-700 text-sm">{item?.joiningDate ? moment(item?.joiningDate).format('DD-MM-YYYY') : 'N/A'}</span>

            )
        },
        {
            key: 'status',
            header: 'Status',
            render: (item: IUser) => (
                <span className={`inline-flex items-center px-2 py-0.5 text-xs font-medium border rounded-full ${item?.status?.toLowerCase() === 'current' ? 'text-green-600 border-green-600 bg-green-50' : 'text-destructive border-destructive bg-destructive/10'}`}>
                    {item?.status.charAt(0).toUpperCase() + item?.status.slice(1)}
                </span>
            )
        },
        {
            key: 'salaryCreated',
            header: 'Salary',
            render: (item: IUser) => (
                <span className={`inline-flex items-center px-2 py-0.5 text-xs font-medium border rounded-full ${item?.isSalaryCreated === true ? 'text-green-600 border-green-600 bg-green-50' : 'text-yellow-600 border-yellow-600 bg-yellow-50'}`}>
                    {item?.isSalaryCreated ? "Created" : "Panding"}
                </span>
            )
        },

        {
            key: 'actions',
            header: 'Actions',
            render: (item: IUser) => (
                <DropdownMenu>
                    <DropdownMenuTrigger asChild>
                        <Button variant="ghost" className="h-8 w-8 p-0 hover:bg-muted">
                            <span className="sr-only">Open menu</span>
                            <MoreHorizontal className="h-4 w-4 text-slate-500" />
                        </Button>
                    </DropdownMenuTrigger>
                    <DropdownMenuContent align="end" className="rounded-xl shadow-lg border-slate-100">
                        <DropdownMenuItem
                            onClick={() => {
                                navigate(`${RoutesLink?.employeeDetail}/${item.id}`);
                            }}
                            className="cursor-pointer"
                        >
                            <Eye className="h-4 w-4" />View Profile
                        </DropdownMenuItem>

                        <DropdownMenuItem
                            onClick={() => navigate(`${RoutesLink.editEmployee}/${item.id}`)}
                            className="cursor-pointer"
                        >
                            <Pencil className="h-4 w-4" />Edit Details
                        </DropdownMenuItem>

                    </DropdownMenuContent>
                </DropdownMenu>
            ),
        },
    ]

    const handleTabChange = (val: string) => {
        const tab = val as "all" | "complete" | "in_process";
        setActiveTab(tab);
        setFilter((prev) => ({
            ...prev,
            isVerified: tab === "all" ? undefined : tab === "complete" ? 1 : 0,
        }));
        setPaginationData((prev) => ({ ...prev, currentPage: 1 }));
    };

    return (
        <Layout>
            <div className='space-y-6'>
                <div className="flex flex-col sm:flex-row justify-between items-start sm:items-center gap-4">
                    <MainHeading title={'Employee'} />
                    <div className="flex flex-wrap gap-2">
                        <Button
                            variant="outline"
                            className="bg-white gap-2"
                            onClick={() => navigate(RoutesLink.bulkImportEmployee)}
                        >
                            <Upload size={16} /> Bulk Onboard
                        </Button>
                        <Button
                            className="gap-2"
                            onClick={() => navigate(RoutesLink?.createEmployee)}
                        >
                            <Plus size={16} /> Start Onboarding
                        </Button>
                    </div>
                </div>

                <Tabs
                    value={activeTab}
                    onValueChange={handleTabChange}
                    className="w-full"
                >
                    <TabsList className="bg-slate-100/80 flex py-5 flex-nowrap overflow-x-auto overflow-y-hidden h-fit gap-2 rounded-lg border border-slate-200/60 shadow-inner mb-6 scroll-bar-hidden items-center overflow-x-auto">
                        <TabsTrigger
                            className="rounded-lg py-3.5 px-3 data-[state=active]:bg-white data-[state=active]:shadow-md data-[state=active]:text-primary data-[state=active]:border-primary/20 border border-transparent flex-1 font-semibold transition-all flex items-center justify-center gap-2 text-slate-500 hover:text-slate-700 hover:bg-slate-200/50 data-[state=active]:hover:bg-white cursor-pointer"
                            value="all"
                        >
                            All Employees
                        </TabsTrigger>
                        <TabsTrigger
                            className="rounded-lg py-3.5 px-3 data-[state=active]:bg-white data-[state=active]:shadow-md data-[state=active]:text-primary data-[state=active]:border-primary/20 border border-transparent flex-1 font-semibold transition-all flex items-center justify-center gap-2 text-slate-500 hover:text-slate-700 hover:bg-slate-200/50 data-[state=active]:hover:bg-white cursor-pointer"
                            value="complete"
                        >
                            Activated
                        </TabsTrigger>
                        <TabsTrigger
                            className="rounded-lg py-3.5 px-3 data-[state=active]:bg-white data-[state=active]:shadow-md data-[state=active]:text-primary data-[state=active]:border-primary/20 border border-transparent flex-1 font-semibold transition-all flex items-center justify-center gap-2 text-slate-500 hover:text-slate-700 hover:bg-slate-200/50 data-[state=active]:hover:bg-white cursor-pointer"
                            value="in_process"
                        >
                            Pending Onboarding
                        </TabsTrigger>
                    </TabsList>

                    <TabsContent value="all">
                        <div className="space-y-4">
                            <CommonFilter
                                searchValue={filter.search || ""}
                                onSearchChange={handleSearchChange}
                                value={filter}
                                filterOptions={STATUS_OPTIONS}
                                filterName="status"
                                filterPlaceholder="Select Status"
                                onChange={handleFilterChange}
                                additionalFilters={filterSelectorsNode}
                            />
                            <CommonTable data={employees} columns={columns} loading={isLoading} stickyColumns={2} />
                            <TablePagination
                                paginationData={paginationData}
                                setPaginationData={setPaginationData}
                                handleChangePageSize={handleChangePageSize}
                                isLoading={isLoading}
                            />
                        </div>
                    </TabsContent>

                    <TabsContent value="complete">
                        <div className="space-y-4">
                            <CommonFilter
                                searchValue={filter.search || ""}
                                onSearchChange={handleSearchChange}
                                value={filter}
                                filterOptions={STATUS_OPTIONS}
                                filterName="status"
                                filterPlaceholder="Select Status"
                                onChange={handleFilterChange}
                                additionalFilters={filterSelectorsNode}
                            />
                            <CommonTable data={employees} columns={columns} loading={isLoading} stickyColumns={2} />
                            <TablePagination
                                paginationData={paginationData}
                                setPaginationData={setPaginationData}
                                handleChangePageSize={handleChangePageSize}
                                isLoading={isLoading}
                            />
                        </div>
                    </TabsContent>

                    <TabsContent value="in_process">
                        <div className="space-y-4">
                            <CommonFilter
                                searchValue={filter.search || ""}
                                onSearchChange={handleSearchChange}
                                value={filter}
                                // filterOptions={STATUS_OPTIONS}
                                // filterName="status"
                                // filterPlaceholder="Select Status"
                                onChange={handleFilterChange}
                                additionalFilters={filterSelectorsNode}
                            />
                            <CommonTable data={employees} columns={columns} loading={isLoading} stickyColumns={2} />
                            <TablePagination
                                paginationData={paginationData}
                                setPaginationData={setPaginationData}
                                handleChangePageSize={handleChangePageSize}
                                isLoading={isLoading}
                            />
                        </div>
                    </TabsContent>
                </Tabs>
            </div>
        </Layout>
    )
}

export default Employees 